import React, { ChangeEvent, FormEvent, useContext, useEffect, useState } from "react";
import CallToken from '../settings/CallToken'
import { RouteComponentProps, useParams } from 'react-router-dom';
import classNames from 'classnames'
import './AccountList.scss'
import Auth from '../services/Auth';
import UserInfo, { CreateUserInfo } from "../viewModels/UserInfo";
import { useHistory } from 'react-router-dom'
import RoleContext, { USER_ROLE, getRoleName } from "../signin/RoleContext";
import { FeatureFlags, FeatureFlagsType } from '../stores/FeatureFlag'

const UserDetails = (props: RouteComponentProps) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaCompare, setMFACompare] = useState("");
  const [mfaConfirm, setMFAConfirm] = useState("");
  const [confirmingMFA, setConfirmingMFA] = useState(false);
  const [userId, setUserId] = useState(-1);
  const [userInfo, setUserInfo] = useState<UserInfo>({
      id: -1,
      lastSignOn: null,
      username: "",
      cnNumber: "",
      fullName: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      altPhoneNumber: "",
      emailAddress: "",
      jobTitle: "",
      location: "",
      previousSignOn: null,
      failedAttemptCount: 0,
      lastChanged: null,
      expiry: null,
      mfaMode: "Email",
      multiFactorContact: "",
      passwordQuestion: "",
      passwordAnswer: "",
      roles: []
    } as UserInfo);

  const [errors, setErrors] = useState([] as string[]);
  const [mfaErrors, setMFAErrors] = useState([] as string[]);
  const [accountErrors, setAccountErrors] = useState([] as string[]);
  const { LPid, UserId } = useParams() as any;

  const { reporting } = useContext(FeatureFlags) as FeatureFlagsType;

  const history = useHistory();

  const roleContext = useContext(RoleContext);

  useEffect(() => {
    if (!UserId || UserId == 'create') {
      setUserId(-1);
    } else {
      setUserId(UserId);
      getAccountData(UserId);
    }

    return;
  }, [UserId]);

  useEffect(() => {
    if (userInfo) {
      setMFAErrors(getMFAErrors(userInfo.mfaMode ?? "None", userInfo.multiFactorContact ?? ""));
      setAccountErrors(getAccountErrors());
    } else {
      setMFAErrors([]);
      setAccountErrors([]);
    }
  }, [userInfo])

  const getAccountData = (id: number) => {
    if (id < 0) {
      return {
        id: -1,
        lastSignOn: null,
        username: "",
        cnNumber: "",
        fullName: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        altPhoneNumber: "",
        emailAddress: "",
        jobTitle: "",
        location: "",
        previousSignOn: null,
        failedAttemptCount: 0,
        lastChanged: null,
        expiry: null,
        mfaMode: "Email",
        multiFactorContact: "",
        passwordQuestion: "",
        passwordAnswer: "",
        roles: []
      } as UserInfo;
    }

    return Auth.getUserInfo(id).then((response) => {
      if (response) { 
        setUserInfo(response);
      }
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    Auth.changePasswordForUser(userId, password)
      .then((response) => {
        if (response.is_error) {
          alert(response.error_content);
        } else {
          alert('password successfully updated');
        }
      });
  };

  const updateUserInfo = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    Auth.updateUserInfo(userInfo).then((response: boolean) => {
      if (response) {
        alert('user successfully updated');
      } else {
        alert('there was an issue updating your information - please refresh the page and try again.'); 
      }
    });
  }

  const createUser = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const createInfo = { ...userInfo } as CreateUserInfo;
      createInfo.password = password;
      createInfo.cnNumber = LPid;
      Auth.createUser(createInfo).then((response : string) => {
        if (response && response.startsWith("User added successfully")) {
          alert('user successfully created');
          history.push(`/accountAdmin/${userInfo.cnNumber}`);
        } else {
          alert('there was an issue creating the user - ' + response);
        }

      });
  }

  const handleMFASubmit = (event : FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (userInfo.mfaMode == "None") {
      submitMFAUpdate();
    } else {
      verifyMFA().then((code) => {
        if (code == "failed") {
          alert('could not update - please verify entered mfa type and contact info and try again');
        } else if (code == "unchanged") {
          //nothing to do
          alert('MFA information matches that already in system.');
          return;
        } else {
          setMFAConfirm("");
          setMFACompare(code);
          setConfirmingMFA(true);
        }
      });
    }
  };

  const submitMFAUpdate = () => {
    Auth.UpdateMFAInfoForUser(userId, userInfo.mfaMode, userInfo.multiFactorContact).then((success: boolean) => {
      if (success) {
        alert('successfully updated')
      } else {
        alert('there was an issue updating your information - please refresh the page and try again.');
      }
    }).catch(() => { alert('error occured updating - please verify information and try again'); }).finally(() => {
      setMFAConfirm("");
      setMFACompare("");
      setConfirmingMFA(false);
    });
  };

  const verifyMFA = async () => {
    const confirmCode = await Auth.getMFAConfirmForUser(userId, userInfo.mfaMode, userInfo.multiFactorContact);
    return confirmCode;
  };

  const handleChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;

    switch (name) {
      case 'mfaConfirm':
        setMFAConfirm(value);
        break;
      case 'password':
        setPassword(value);
        setErrors(getErrors(value));
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
    }
  };

  const cancelMFAVerify = () => {
    setConfirmingMFA(false);
  };

  const handleUserDetailChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    let value = event.target.value;
    const newState = { ...userInfo };

    if (name === "phoneNumber" || name === "altPhoneNumber" && value) {
        //phone masking
        value = value.replace(/[^0-9]/g, '');

        if (value.length <= 3) {
          //do nothing
        }
        else if (value.length > 3 && value.length < 7) {
          value = `${value.substring(0, 3)}-${value.substring(3)}`
        } else {
          value = `${value.substring(0, 3)}-${value.substring(3, 6)}-${value.substring(6, Math.min(10, value.length))}`
        }
    }

    (newState as any)[name] = value;

    const acctErrors = getAccountErrors();
    setAccountErrors(acctErrors);
    setUserInfo(newState);
  }

  const handleRoleChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    const newState = { ...userInfo };

    if (!event.target.checked) {
      //de-selected
      if (newState.roles && newState.roles.some(a => a == value)) {
        newState.roles = newState.roles.filter(a => a != value);
      }
    } else {
      //selected
      if (!newState.roles) {
        newState.roles = [value];
      } else if (!newState.roles.some(a => a == value)) {
        //remove existing user types, re-apply new. Done to avoid full clear that would affect non usertype roles
        if (event.target.name == "userRole") {
          newState.roles = newState.roles.filter(a => !userType.some(b => b.value == a));
        }

        if (!isNaN(value) && value >= 0) {
          newState.roles.push(value);
        }
      }
    }

    setUserInfo(newState);
  }


  const handleMFAChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    let value = event.target.value;
    const newState = { ...userInfo };
    (newState as any)[name] = value;

    if (name === "multiFactorContact" && userInfo.mfaMode == "SMS") {
      if (value) {
        //phone masking
        value = value.replace(/[^0-9]/g, '');

        if (value.length <= 3) {
          //do nothing
        }
        else if (value.length > 3 && value.length < 7) {
          value = `${value.substring(0, 3)}-${value.substring(3)}`
        } else {
          value = `${value.substring(0, 3)}-${value.substring(3, 6)}-${value.substring(6, Math.min(10, value.length))}`
        }
      }

      newState[name] = value;
    } 

    setUserInfo(newState);
    setMFAErrors(errors);
  };

  const getMFAErrors = (mode: string, value:string) : string[] => {
    const newErrors = [] as string[];

    if (mode == "None") {
      return newErrors;
    }

    if (!value || value.trim().length == 0) {
      newErrors.push("MFA Contact must be provided");
    }


    if (value && mode == "Email") {
      if ((value.indexOf('@') <= 0) || (value.split('@').length < 2)) {
        newErrors.push("Email entered is invalid");
      }
    } else if (value && mode == "SMS") {
      if (!/(\d){3}-(\d){3}-(\d){4}/.test(value)) {
        newErrors.push("Phone number entered is invalid. Please ensure of form 123-456-7890");
      }
    }

    return newErrors;
  };

  const getErrors = (newPassword : string) => {
    const newErrors = [] as string[];

    if (!/[a-z]+/.test(newPassword)) {
      newErrors.push("Password must contain at least one lowercase letter");
    }

    if (!/[A-Z]+/.test(newPassword)) {
      newErrors.push("Password must contain at least one uppercase letter");
    }

    if (!/[0-9]+/.test(newPassword)) {
      newErrors.push("Password must contain at least one number");
    }

    if (!/["@\\|!#$%&/()=?{}.\-;'^<>_,*]+/.test(newPassword)) {
      newErrors.push("Password must contain at least one special letter (from \"@\\|!#$%&/()=?{}.-;'^<>_,*)");
    }

    if (newPassword.length > 0 && newPassword.length < 8) {

      newErrors.push("Your password must be a least 8 characters long.");
    }

    if (/ /.test(newPassword)) {
      newErrors.push("Password cannot contain spaces");
    }

    return newErrors;
  };

  const isPasswordValid = () => {
    let retVal = true;

    if (!/[a-z]+/.test(password)) {
      retVal = false;
    }

    if (!/[A-Z]+/.test(password)) {
      retVal = false;
    }

    if (!/[0-9]+/.test(password)) {
      retVal = false;
    }

    if (!/["@\\|!#$%&/()=?{}.\-;'^<>_,*]+/.test(password)) {
      retVal = false;
    }

    if (password.length > 0 && password.length < 8) {
      retVal = false;
    }

    if (/ /.test(password)) {
      retVal = false;
    }

    return retVal;
  };

  const isConfirmPasswordValid = () => {
    if (isPasswordValid()) {
      if (password === confirmPassword) {
        return true;
      }
    }
    return false;
  };

  const isMFAValid = () => {
    if (userInfo.mfaMode == "None") {
      return true;
    }

    let isValid = true;

    if (!userInfo.multiFactorContact || userInfo.multiFactorContact.trim().length == 0) {
      isValid = false;
    }

    if (userInfo.multiFactorContact && userInfo.mfaMode == "Email") {
      if ((userInfo.multiFactorContact.indexOf('@') <= 0) || (userInfo.multiFactorContact.split('@').length < 2)) {
        isValid = false;
      }
    } else if (userInfo.multiFactorContact && userInfo.mfaMode == "SMS") {
      if (!/(\d){3}-(\d){3}-(\d){4}/.test(userInfo.multiFactorContact)) {
        isValid = false;
      }
    }

    return userInfo.multiFactorContact && (userInfo.multiFactorContact.trim().length > 0) && isValid;
  };

  const confirmedMFA = () => {
    return confirmingMFA && (mfaConfirm === mfaCompare);
  };

  const isButtonDisabled = () => {
    return (!isConfirmPasswordValid() || password.length < 1)
  };

  const canUpdateUser = () : boolean => {
    let isValid = true;

    if (!userInfo.username) {
      isValid = false;
    } else if (userInfo.username.indexOf(' ') >= 0) {
      isValid = false;
    } else if (userInfo.username.length > 20) {
      isValid = false;
    } else if (userInfo.username.indexOf('@') >= 0) {
      isValid = false;
    }

    if (userInfo.emailAddress.length === 0) {
      isValid = false;
    }

    if (userInfo.firstName.length === 0) {
      isValid = false;
    }
    if (userInfo.lastName.length === 0) {
      isValid = false;
    }
    if (!userInfo.phoneNumber.match(/^\d{3}-\d{3}-\d{4}?/)) {
      isValid = false;
    }

    if (userInfo.altPhoneNumber && !userInfo.altPhoneNumber.match(/^\d{3}-\d{3}-\d{4}?/)) {
      isValid = false;
    }

    if (userInfo.passwordAnswer?.length == 0) {
      isValid = false;
    }

    if (userInfo.passwordQuestion?.length == 0) {
      isValid = false;
    }

    return isValid;
  }

  const getAccountErrors = () : string[] => {
    const errors = [] as string[];

    if (!userInfo.username) {
      errors.push("username cannot be blank");
    } else if (userInfo.username.indexOf(' ') >= 0) {
      errors.push("username cannot contain a space");
    } else if (userInfo.username.length > 20) {
      errors.push('username cannot be longer than 20 characters');
    } else if (userInfo.username.indexOf('@') >= 0) {
      errors.push('Username cannot contain the @ symbol');
    }

    if (userInfo.emailAddress.length === 0) {
      errors.push('Please enter an email address.');
    }

    if (userInfo.firstName.length === 0) {
      errors.push('Please enter a first name.');
    }
    if (userInfo.lastName.length === 0) {
      errors.push('Please enter a last name.');
    }
    if (!userInfo.phoneNumber.match(/^\d{3}-\d{3}-\d{4}?/)) {
      errors.push('Please enter a phone number like 999-888-7777.');
    }

    if (userInfo.altPhoneNumber && !userInfo.altPhoneNumber.match(/^\d{3}-\d{3}-\d{4}?/)) {
      errors.push('Please enter an alternate phone number like 999-888-7777.');
    }

    if (userInfo.passwordAnswer?.length == 0) {
      errors.push('Password answer must not be blank.');
    }

    if (userInfo.passwordQuestion?.length == 0) {
      errors.push('Password question must not be blank.');
    }

    return errors;
  }

  const passwordChangeRequired = localStorage.getItem("PasswordChangeRequired") == "true";

  const userType = [{ name: USER_ROLE.ADMIN, value: 22 }, { name: USER_ROLE.AGLC, value: 21 }];
  const additionalRoles = reporting ? [{ name: USER_ROLE.RUNREPORTS, value: 8 }, { name: USER_ROLE.MANAGEUSERS, value: 20 }] : [{ name: USER_ROLE.MANAGEUSERS, value: 20 }];

  return (
      <div className="container">
          <div className="row">
              <div className="col-12">
                  <h1>Account Settings</h1>
              </div>
      </div>
    {passwordChangeRequired && <div className="row">
          <div className="col-12">
              <span className="text-danger">Your password was recently reset. You will need to change your password before accessing other functionality.</span>
          </div>
      </div>}
      {userId < 0 ? '' : <>
        <div className="row accountInfo">
          <div className="col-5">
            <h4>Organization Information</h4>
          </div>
          <div className="col-7">
            <div><label>Cannabis Number:</label><span>{userInfo.cnNumber}</span></div>
            <div><label>Name:</label><span>{userInfo.username}</span></div>
          </div>
        </div>
        <div className="row accountInfo">
          <div className="col-5">
            <h4>Login Information</h4>
          </div>
          <div className="col-7">
            <div><label>Account Number:</label><span>{userId}</span></div>
            <div><label>This Session Signed On:</label><span>{userInfo.lastSignOn ? userInfo.lastSignOn.toLocaleString() : ""}</span></div>
            <div><label>Your Previous Sign-On:</label><span>{userInfo.previousSignOn ? userInfo.previousSignOn.toLocaleString() : ""}</span></div>
            <div><label>Recent Failed Attempts:</label><span>{userInfo.failedAttemptCount}</span></div>
            <div><label>Password Last Changed:</label><span>{userInfo.lastChanged ? userInfo.lastChanged.toLocaleString() : ""}</span></div>
            <div><label>Password Expires (Days):</label><span>{userInfo.expiry ? (Math.round((userInfo.expiry.valueOf() - (new Date()).valueOf()) / (1000 * 60 * 60 * 24)) + " Days") : ""}</span></div>
          </div>
        </div></>}
        <div className="row accountInfo">
          <div className="col-12">
            <h4>User Information</h4>
        </div>
        <form onSubmit={updateUserInfo} className="col-12">
          <CallToken />
          <div className="row">
            <div className="form-group col-5"><label className="form-control-label">Username:</label><input className={classNames({ "form-control": true, 'is-valid': userInfo.username, 'is-invalid': !userInfo.username })} type="text" value={userInfo.username} onChange={handleUserDetailChanged} name="username" required disabled={ UserId > 0 } /></div>
            <div className="form-group col-7"><label className="form-control-label">Email Address:</label><input className={classNames({ "form-control": true, 'is-valid': userInfo.emailAddress, 'is-invalid': !userInfo.emailAddress })} type="email" value={userInfo.emailAddress} onChange={handleUserDetailChanged} name="emailAddress" /></div>
          </div>
          <div className="row">
            <div className="form-group col-5"><label className="form-control-label">First Name:</label><input className={classNames({ "form-control": true, 'is-valid': userInfo.firstName, 'is-invalid': !userInfo.firstName })} type="text" value={userInfo.firstName} onChange={handleUserDetailChanged} name="firstName" required /></div>
            <div className="form-group col-7"><label className="form-control-label">Last Name:</label><input className={classNames({ "form-control": true, 'is-valid': userInfo.lastName, 'is-invalid': !userInfo.lastName })} type="text" value={userInfo.lastName} onChange={handleUserDetailChanged} name="lastName" /></div>
          </div>
          <div className="row">
            <div className="form-group col-5"><label className="form-control-label">Phone Number:</label><input className={classNames({ "form-control": true, 'is-valid': userInfo.phoneNumber, 'is-invalid': !userInfo.phoneNumber })} type="tel" value={userInfo.phoneNumber} onChange={handleUserDetailChanged} name="phoneNumber" required /></div>
            <div className="form-group col-7"><label className="form-control-label">Alt Phone Number:</label><input className="form-control" type="tel" value={userInfo.altPhoneNumber} onChange={handleUserDetailChanged} name="altPhoneNumber" /></div>
          </div>
          <div className="row">
            <div className="form-group col-5"><label className="form-control-label">Job Title:</label><input className="form-control" type="text" value={userInfo.jobTitle} onChange={handleUserDetailChanged} name="jobTitle" /></div>
            <div className="form-group col-7"><label className="form-control-label">Location:</label><input className="form-control" type="text" value={userInfo.location} onChange={handleUserDetailChanged} name="location" /></div>
          </div>
          <div className="row">
            <div className="form-group col-5">
              <label htmlFor="passwordQuestion" className="form-control-label">Password Question</label>
              <input value={userInfo.passwordQuestion ?? ""} name="passwordQuestion" type="text" id="passwordQuestion" className={classNames({ "form-control": true, 'is-valid': userInfo.passwordQuestion, 'is-invalid': !userInfo.firstName })} placeholder="Password Question"
                onChange={handleUserDetailChanged} required />
            </div>
            <div className="form-group col-7"><label htmlFor="passwordAnswer" className="form-control-label">Answer</label>
              <input value={userInfo.passwordAnswer ?? ""} name="passwordAnswer" type="text" id="passwordAnswer" className={classNames({ "form-control": true, 'is-valid': userInfo.passwordAnswer, 'is-invalid': !userInfo.firstName })} placeholder="Password Answer"
                onChange={handleUserDetailChanged} required />
            </div>
          </div>
          {Auth.checkRoleMembership([USER_ROLE.ADMIN, USER_ROLE.MANAGEUSERS]) && <><div className="row">
              <div className="col-12">
                <h4>Roles</h4>
              </div>
          </div>
          <div className="row roleInfo">
              {Auth.checkRoleMembership([USER_ROLE.ADMIN]) && <div className="form-group col-5">
              <label className="form-control-label authTitle">User Type</label>
              <ul>
                { /* all users have role so effect is clearing the other admin roles */}
                <li key={"Default"}><label><input type="radio" value="" name="userRole" onChange={handleRoleChanged} checked={!userType.some(a => userInfo.roles && userInfo.roles.some(b => b == a.value))} />{getRoleName(USER_ROLE.DEFAULT)}</label></li>
                {userType.map(role => <li key={role.name.toString()}><label><input type="radio" value={role.value} name="userRole" onChange={handleRoleChanged} checked={userInfo.roles?.some(a => a == role.value)} />{getRoleName(role.name)}</label></li>)}
              </ul>
            </div>}
              <div className="form-group col-7">
                <label className="form-control-label authTitle">Additional Permissions</label>
                <ul>
                {additionalRoles.map(role => <li key={role.name.toString()}><label><input type="checkbox" value={role.value} name="additionalRole" onChange={handleRoleChanged} checked={userInfo.roles?.some(a => a == role.value)} />{getRoleName(role.name)}</label></li>)}
                </ul>
            </div>
          </div></>}
          {!UserId ? '' : <div className="row">
            <button type="submit" className="btn btn-orange btn-block" disabled={!canUpdateUser()}>Update User</button></div>}
          <div className="row"><div className="invalid-feedback col-12" style={{ display: !canUpdateUser() ? "block" : "none" }}>
            {accountErrors.map(item => <div key={item}>{item}</div>)}
          </div></div>
        </form>
        </div>
          {confirmingMFA ?
            <div className="row accountInfo confirmMFADialog">
              <div>
                <h4>Verify Multi Factor Auth</h4>
                <br />
                <p>A code has been sent to you - please enter it below to verify you received it. If you did not receive a code, please press cancel and verify your information</p>
                <label>Enter your code:</label>
                <input type="text" className="form-control" name="mfaConfirm" onChange={handleChanged} value={mfaConfirm} placeholder="Enter MFA Code Sent" />
                <br />
                <input type="submit" className="btn btn-orange" disabled={!confirmedMFA()} onClick={submitMFAUpdate} />
                <input type="button" className="btn btn-orange" value="Cancel" onClick={cancelMFAVerify}></input>
              </div>
            </div> : ''}
      {!UserId ? '' : <form onSubmit={handleMFASubmit} className="mfaInfo">
                <CallToken />
                <div className="row accountInfo">
                    <div className="col-12">
                        <h4 >Multi Factor Authentication</h4>
                    </div>
                    <div className="col-5">
                        <label className="form-control-label authTitle">Authentication Method</label>
            <ul>
              {(Auth.checkRoleMembership([USER_ROLE.ADMIN, USER_ROLE.AGLC]) ? ["None", "Email", "SMS"] : ["Email", "SMS"]).map(a => { return <li key={a} ><label><input type="radio" value={a} name="mfaMode" onChange={handleMFAChanged} checked={a == userInfo.mfaMode} />{a}</label></li> })}
                        </ul>
                    </div>
                    <div className="col-7">
                        <label className="form-control-label">Email or SMS:&nbsp;</label>
                        <input
                            value={userInfo.multiFactorContact ?? ''}
                            name="multiFactorContact"
                            className={classNames({
                                'form-control': true,
                                'is-valid': isMFAValid(),
                                'is-invalid': !isMFAValid()
                            })}
                            placeholder="Contact for MFA"
                            onChange={handleMFAChanged}
                            required={userInfo.mfaMode !== "None"}
                        />
                        <div className="invalid-feedback">
                            {mfaErrors.map(item => <div key={item}>{item}</div>)}
                        </div>
                        <button type="submit" className="btn btn-orange btn-block" disabled={!isMFAValid()}>Update 2FA</button>
                    </div>
                    <div className="col-12">
                    </div>
                </div>
            </form>}
          <div className="row">
              <div className="col-5">
                  {!UserId ? '' : <h4>Change Password</h4>}
                  <ul>
                      <li>Passwords must be at least 8 characters long</li>
                      <li>It must contain at least:
                          <ul>
                              <li>1 uppercase letter</li>
                              <li>1 lowercase letter</li>
                              <li>1 number</li>
                              <li>1 special character.</li>
                              <li>no spaces</li>
                              <li>Not been previously used</li>
                          </ul>
                      </li>
                  </ul>                            
              </div>
              <div className="col-7">
                  <form onSubmit={handleSubmit}>
                      <div className="form-group">
                          <label htmlFor="inputPassword" className="form-control-label">
                          {UserId ? "Change" : "New"} Password
                          </label>
                          <input
                              value={password}
                              name="password"
                              type="password"
                              id="inputPassword"
                              className={classNames({
                                  'form-control': true,
                                  'is-valid': isPasswordValid(),
                                  'is-invalid': !isPasswordValid()
                              })}
                              placeholder="Password"
                              onChange={handleChanged}
                              required
                          />
                          <div className="invalid-feedback">
                              {errors.map(item => <div key={ item }>{item}</div>) }
                          </div>
                      </div>
                      <div className="form-group">
                          <label htmlFor="confirmPassword" className="form-control-label">
                              Confirm New Password
                          </label>
                          <input
                              value={confirmPassword}
                              name="confirmPassword"
                              type="password"
                              id="confirmPassword"
                              className={classNames({
                                  'form-control': true,
                                  'is-valid': isConfirmPasswordValid(),
                                  'is-invalid': !isConfirmPasswordValid()
                              })}
                              placeholder="Confirm Password"
                              onChange={handleChanged}
                              required
                          />
                          <div className="invalid-feedback">
                              Your passwords must match.
                          </div>
                      </div>
                      <div className="form-group">
              {UserId ? <button type="submit" className="btn btn-orange btn-block" disabled={isButtonDisabled()}>Change Password</button>
                : <button type="button" className="btn btn-orange btn-block" onClick={createUser} disabled={!canUpdateUser() || isButtonDisabled()}>Create User</button>}
                      </div>
                  </form>
              </div>
        </div>
      </div>);
}


export default UserDetails;
